import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import orientationService from '../../../api/services/orientationService';
import locationService from '../../../api/services/locationService';
import toast from 'react-hot-toast';
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import WarningPopup from "../../../components/WarningPopup";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";

import {
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Video,
  Search,
  Download,
  Eye,
  Edit,
  Plus,
  Upload,
  Send,
  Trash2,
  Play,
  X,
  FileText,
  Square,
  CheckSquare,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import BulkUploadModal from '../../../components/common/BulkUploadModal';

const TAB_STATUS_MAP = {
  scheduled: 'scheduled',
  upcoming: 'scheduled',
  ongoing: 'ongoing',
  completed: 'completed',
  cancelled: 'cancelled',
};

const Orientation = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, canCreate } = usePermissions();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterType, setFilterType] = useState(searchParams.get('type') || "all");
  const [filterLocation, setFilterLocation] = useState(searchParams.get('location') || "all");
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || "all");
  const [orientations, setOrientations] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrientationId, setDeleteOrientationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || "");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statistics, setStatistics] = useState({
    total: 0,
    scheduled: 0,
    ongoing: 0,
    completed: 0,
    cancelled: 0
  });

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Handle Sort Request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper for sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4 text-agrosaf-green ml-1" />;
    return <ArrowDown className="h-4 w-4 text-agrosaf-green ml-1" />;
  };

  // Get Sorted Data (backend handles sorting now)
  const sortedOrientations = React.useMemo(() => {
    return orientations;
  }, [orientations]);

  // Truncate helper for tidy listing
  const truncate = (value, max = 30) => {
    if (!value && value !== 0) return "";
    let str = "";
    if (typeof value === 'object' && value !== null) {
      str = value.name || value.fullAddress || String(value);
    } else {
      str = String(value);
    }
    return str.length > max ? str.slice(0, max - 1) + "…" : str;
  };

  // Debounce search term with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Permission-based access control functions
  const canExportData = () => {
    return hasPermission('onboarding', 'read');
  };

  const canCreateOrientation = () => {
    return canCreate('onboarding');
  };

  const canViewOrientation = () => {
    return hasPermission('onboarding', 'read');
  };

  const canEditOrientation = () => {
    return hasPermission('onboarding', 'update');
  };

  const canDeleteOrientation = () => {
    return hasPermission('onboarding', 'delete');
  };

  const canSendInvites = () => {
    return hasPermission('onboarding', 'create') || hasPermission('onboarding', 'update');
  };

  const canCloneOrientation = () => {
    return canCreate('onboarding');
  };

  // Reset page when filters/tab/search/date/sort change
  useEffect(() => {
    setPage(1);
  }, [activeTab, filterType, filterLocation, debouncedSearchTerm, dateRange.startDate, dateRange.endDate, sortConfig]);

  // Load orientations from API
  useEffect(() => {
    loadOrientations();
    loadLocations();
  }, [activeTab, filterType, filterLocation, debouncedSearchTerm, dateRange.startDate, dateRange.endDate, page, pageSize, sortConfig]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component (excluding 'tab' as it's internal state)
      const managedKeys = ['search', 'type', 'location', 'startDate', 'endDate', 'page', 'limit', 'status'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterType && filterType !== 'all') params.set('type', filterType);
      if (filterLocation && filterLocation !== 'all') params.set('location', filterLocation);
      if (activeTab && activeTab !== 'all') params.set('status', activeTab);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (pageSize && pageSize !== 10) params.set('limit', String(pageSize));

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, filterType, filterLocation, activeTab, dateRange, page, pageSize, setSearchParams]);

  // Load locations from backend
  const loadLocations = async () => {
    try {
      const result = await locationService.getActiveLocations();

      if (result.success) {
        const locationNames = result.data.map(loc => {
          if (typeof loc === 'string') return loc;
          if (loc && typeof loc === 'object') {
            if (loc.value) return loc.value;
            return loc.name || loc.label || loc.value || String(loc);
          }
          return String(loc);
        });
        setLocations(locationNames);
      } else {
        // Fallback to hardcoded locations
        setLocations(['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote']);
      }
    } catch (err) {
      // Fallback to hardcoded locations
      setLocations(['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote']);
    }
  };

  const loadOrientations = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { page, limit: pageSize };
      const mappedStatus = TAB_STATUS_MAP[activeTab];
      if (mappedStatus) params.status = mappedStatus;
      if (filterType !== "all") params.type = filterType;
      if (filterLocation !== 'all') params.location = filterLocation;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      if (sortConfig.key) {
        params.sortBy = sortConfig.key;
        params.sortOrder = sortConfig.direction;
      }

      const result = await orientationService.getOrientations(params);

      if (result && result.responseCode === 200 && result.data) {
        setOrientations(result.data.orientations || []);
        const p = result.data.pagination || {};
        // normalize keys
        const current = p.current || p.currentPage || p.page || page;
        const pages = p.pages || p.totalPages || totalPages;
        const total = p.total || p.totalItems || totalItems;
        const limit = p.limit || p.itemsPerPage || pageSize;
        if (current && current !== page) setPage(current);
        if (limit && limit !== pageSize) setPageSize(limit);
        setTotalPages(pages || 1);
        setTotalItems(Number.isFinite(total) ? total : (result.data.orientations?.length || 0));

        // Extract and set statistics from API response
        if (result.data.statistics) {
          setStatistics({
            total: result.data.statistics.total || 0,
            scheduled: result.data.statistics.scheduled || 0,
            ongoing: result.data.statistics.ongoing || 0,
            completed: result.data.statistics.completed || 0,
            cancelled: result.data.statistics.cancelled || 0
          });
        }
      } else {
        const errorMsg = result?.responseMessage || result?.message || "Failed to load orientations";
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error?.responseData?.responseMessage ||
        error?.response?.data?.responseMessage ||
        error?.message ||
        "Error loading orientations";
      setError(errorMsg);
      if (!error?.isRateLimited) {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // State for selection and export
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Reset selection when filters/search/page change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [page, filterType, filterLocation, debouncedSearchTerm, activeTab, dateRange.startDate, dateRange.endDate]);

  // Selection Handlers
  const toggleRowSelection = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    const currentPageIds = orientations.map(o => o.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const baseExportFilters = useMemo(() => {
    const f = {};
    const mapped = TAB_STATUS_MAP[activeTab];
    if (mapped) f.status = mapped;
    if (filterType !== 'all') f.type = filterType;
    if (filterLocation !== 'all') f.location = filterLocation;
    const trimmed = debouncedSearchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    return f;
  }, [
    activeTab,
    filterType,
    filterLocation,
    debouncedSearchTerm,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const exportSortBy = useMemo(() => {
    if (sortConfig.key === 'date') return 'date';
    if (sortConfig.key === 'title') return 'title';
    if (sortConfig.key === 'status') return 'status';
    return 'date';
  }, [sortConfig.key]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module: 'orientation',
          format,
          scope,
          filters: overrideFilters ?? baseExportFilters,
          page: overridePage ?? page,
          limit: overrideLimit ?? pageSize,
          sortBy: exportSortBy,
          sortOrder: sortConfig.direction,
          selectedIds,
        }),
      }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `orientation-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, pageSize, exportSortBy, sortConfig.direction]
  );

  const handleOrientationSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected sessions to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} session(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleOrientationScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !orientations.length) {
          toast.error('No sessions to export on the current page.');
          return;
        }

        if (scope === 'filtered' && totalItems === 0) {
          toast.error('No sessions match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [orientations.length, totalItems, baseExportFilters, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleCreateOrientation = () => {
    navigate("/create_orientation");
  };

  const handleViewOrientation = (orientationId) => {
    navigate(`/view_orientation/${orientationId}`);
  };

  const handleEditOrientation = (orientationId) => {
    navigate(`/create_orientation/${orientationId}`);
  };

  const handleDeleteOrientation = (orientationId) => {
    setDeleteOrientationId(orientationId);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteOrientation = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrientation = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        const idsToDelete = Array.from(selectedRows);
        let successCount = 0;
        let errorCount = 0;

        for (const id of idsToDelete) {
          try {
            const result = await orientationService.deleteOrientation(id);
            if (result && result.responseCode === 200) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} orientations deleted successfully`);
          setSelectedRows(new Set());
          loadOrientations();
        }
        if (errorCount > 0) {
          toast.error(`Failed to delete ${errorCount} orientations`);
        }
      } else {
        if (!deleteOrientationId) return;
        const result = await orientationService.deleteOrientation(deleteOrientationId);
        if (result && result.responseCode === 200) {
          toast.success('Orientation deleted successfully');
          loadOrientations();
        } else {
          toast.error(result?.responseMessage || 'Failed to delete orientation');
        }
      }
      setShowDeleteModal(false);
      setDeleteOrientationId(null);
      setIsBulkDelete(false);
    } catch (error) {
      toast.error(
        error?.responseData?.responseMessage ||
        error?.response?.data?.responseMessage ||
        error?.message ||
        'An error occurred while deleting'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteOrientation = () => {
    setShowDeleteModal(false);
    setDeleteOrientationId(null);
    setIsBulkDelete(false);
  };

  const handleSendInvites = (orientationId) => {
    navigate(`/send_orientation_invites/${orientationId}`);
  };

  const handleCloneOrientation = (orientationId) => {
    navigate(`/clone_orientation/${orientationId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "ongoing":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "scheduled":
        return <Calendar className="h-4 w-4" />;
      case "ongoing":
        return <Play className="h-4 w-4" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "cancelled":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "in_person":
        return <MapPin className="h-4 w-4" />;
      case "virtual":
        return <Video className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Handle formats like "14:30" or "14:30:00"
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];

      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  if (loading && isInitialLoad) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Orientations
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="onboarding" action="read">
            <Button
              onClick={loadOrientations}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Orientation Management
          </h1>
          <p className="text-gray-600">
            Schedule and manage employee orientation sessions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedRows.size > 0 && canDeleteOrientation() && (
            <PermissionGate permission="onboarding" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteOrientation}
                className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}

          {canExportData() && (
            <PermissionGate permission="onboarding" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={<Download className={`h-4 w-4 ${exportLoading ? 'animate-spin' : ''}`} />}
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={`flex items-center space-x-2 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}`}
                >
                  <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleOrientationSelectedFormatExport}
                />
              </div>
            </PermissionGate>
          )}

          {canCreateOrientation() && (
            <PermissionGate permission="onboarding" action="create">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center space-x-2"
              >
                Bulk Upload
              </Button>
            </PermissionGate>
          )}

          {canCreateOrientation() && (
            <PermissionGate permission="onboarding" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleCreateOrientation}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Create Orientation
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Sessions
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(statistics.total)}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(statistics.scheduled)}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ongoing</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(statistics.ongoing)}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Play className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(statistics.completed)}
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex flex-wrap items-end gap-4 w-full mb-6">
            {/* Search */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by Session Detail..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and common search/document symbols
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and common search/document symbols
                  if (!/^[a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                autoFocus
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              />
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All</option>
                <option value="scheduled">Scheduled</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All Types</option>
                <option value="in_person">In-Person</option>
                <option value="virtual">Virtual</option>
              </select>
            </div>

            {/* Location Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location?.replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                allowFutureDates={true}
                onChange={(range) => {
                  setDateRange(range);
                  setPage(1);
                }}
              />
            </div>

            {/* Clear Filters */}
            {(searchTerm || filterType !== "all" || filterLocation !== "all" || activeTab !== "all" || dateRange.startDate || dateRange.endDate) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={() => {
                    setSearchTerm("");
                    setFilterType("all");
                    setFilterLocation("all");
                    setActiveTab("all");
                    setDateRange({ startDate: '', endDate: '' });
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Orientations List */}
          {!loading && orientations.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No orientations found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterType !== "all" || filterLocation !== "all" || activeTab !== "all" || dateRange.startDate || dateRange.endDate
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by creating your first orientation session.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left w-12">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                        title={selectedRows.size === orientations.length ? "Deselect All" : "Select All"}
                      >
                        {orientations.length > 0 && orientations.every(o => selectedRows.has(o.id)) ? (
                          <CheckSquare className="h-5 w-5 text-agrosaf-green" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('title')}
                      title="Sort by Title"
                    >
                      <div className="flex items-center">
                        Session Details
                        {getSortIcon('title')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('type')}
                      title="Sort by Type"
                    >
                      <div className="flex items-center">
                        Type & Location
                        {getSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('status')}
                      title="Sort by Status"
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('date')}
                      title="Sort by Date"
                    >
                      <div className="flex items-center">
                        Schedule
                        {getSortIcon('date')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('createdAt')}
                      title="Sort by Created At"
                    >
                      <div className="flex items-center">
                        Created At
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Available actions: View, Edit, Send Invites, or Delete orientation"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    [...Array(5)].map((_, index) => (
                      <tr key={index} className="animate-pulse">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-5 w-5 bg-gray-200 rounded"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-48"></div>
                            <div className="h-3 bg-gray-200 rounded w-64"></div>
                            <div className="h-3 bg-gray-200 rounded w-32"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-28"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <div className="h-5 w-5 bg-gray-200 rounded"></div>
                            <div className="h-5 w-5 bg-gray-200 rounded"></div>
                            <div className="h-5 w-5 bg-gray-200 rounded"></div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    sortedOrientations.map((orientation) => (
                      <tr key={orientation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowSelection(orientation.id);
                            }}
                            className={`focus:outline-none transition-colors ${selectedRows.has(orientation.id) ? 'text-agrosaf-green' : 'text-gray-400 hover:text-gray-600'
                              }`}
                          >
                            {selectedRows.has(orientation.id) ? (
                              <CheckSquare className="h-5 w-5" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{truncate(orientation.title?.replace(/\b\w/g, l => l.toUpperCase()), 60)}</div>
                            <div className="text-sm text-gray-500">{truncate(orientation.description, 60)}</div>
                            <div className="text-xs text-gray-400">
                              Trainer: {truncate(
                                Array.isArray(orientation.trainerData) && orientation.trainerData.length > 0
                                  ? orientation.trainerData.map(t => t.name).join(', ')
                                  : (Array.isArray(orientation.trainer) ? orientation.trainer.join(', ') : (orientation.trainer || 'N/A')),
                                60
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getTypeIcon(orientation.type)}
                            <div className="ml-2">
                              <div className="text-sm text-gray-900 capitalize">
                                {orientation.type.replace("_", " ")}
                              </div>
                              <div className="text-sm text-gray-500">
                                {truncate(orientation.location, 24)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              orientation.status
                            )}`}
                          >
                            {getStatusIcon(orientation.status)}
                            <span className="ml-1 capitalize">
                              {orientation.status}
                            </span>
                          </span>
                          {orientation.reason && (
                            <div className="text-xs text-red-600 mt-1">
                              {truncate(orientation.reason, 48)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">
                              {formatDate(orientation.date, { includeTime: false }) || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              at {formatTimeToAMPM(orientation.time)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {orientation.duration === 0.5 ? '30 min' : `${orientation.duration} ${orientation.duration === 1 ? 'hour' : 'hours'}`}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {orientation.createdAt ? formatDate(orientation.createdAt, { includeTime: true }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {canViewOrientation() && (
                              <PermissionGate permission="onboarding" action="read">
                                <button
                                  onClick={() =>
                                    handleViewOrientation(orientation.id)
                                  }
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View Orientation Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {canEditOrientation() &&
                              orientation.status !== "completed" && (
                                <PermissionGate permission="onboarding" action="update">
                                  <button
                                    onClick={() =>
                                      handleEditOrientation(orientation.id)
                                    }
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title="Edit Orientation"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}
                            {canSendInvites() &&
                              orientation.status === "scheduled" && (
                                <PermissionGate permission="onboarding" action="update">
                                  <button
                                    onClick={() => handleSendInvites(orientation.id)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Send Invites"
                                  >
                                    <Send className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}

                            {canDeleteOrientation() &&
                              (orientation.status === "scheduled" ||
                                orientation.status === "completed" ||
                                orientation.status === "cancelled") && (
                                <PermissionGate permission="onboarding" action="delete">
                                  <button
                                    onClick={() =>
                                      handleDeleteOrientation(orientation.id)
                                    }
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete Orientation"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination Footer */}
          {totalItems > pageSize && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                limit={pageSize}
                hasNextPage={page < totalPages}
                hasPrevPage={page > 1}
                onPageChange={setPage}
                onLimitChange={(l) => { setPageSize(Number(l)); setPage(1); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* WarningPopup for Deletion */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={cancelDeleteOrientation}
        onConfirm={confirmDeleteOrientation}
        title={isBulkDelete ? "Delete Orientations" : "Delete Orientation"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedRows.size} selected orientation sessions? This action cannot be undone.`
          : `Are you sure you want to delete this orientation session? This action cannot be undone.`
        }
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Orientation Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleOrientationScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => loadOrientations()}
          config={{
            title: 'Bulk Upload Orientations',
            subtitle: 'Import multiple orientation sessions using a CSV file',
            templateEndpoint: '/orientation/bulk-upload/template',
            validateEndpoint: '/orientation/bulk-upload/validate',
            uploadEndpoint: '/orientation/bulk-upload',
            templateFilename: 'orientation_bulk_upload_template.csv',
            dataKey: 'orientations',
            instructions: [
              'Fill in orientation details in the CSV',
              'Fields: title, description, type, location, date, time, duration, capacity, trainer, department',
              'Ensure date format is YYYY-MM-DD'
            ]
          }}
        />
      )}
    </div>
  );
};

export default Orientation;
